import type {
  CourseResponse,
  LessonResponse,
  ExitQuizApiResponse,
  ExitQuizApiQuestion,
  ExitQuizApiOption,
  ExitQuizSubmitResult,
  PlayerAuthCheckResponse,
} from "@/types/api";

const WHISPER_TIMEOUT_MS = 20000;

/** When embed omits `LP_BASE_URL`, avoid same-origin `/api/...` on the wrong host (e.g. course.*). */
const FALLBACK_API_BASE = "https://community.gcsewithrosi.co.uk";

/**
 * Resolved API origin (CodeIgniter). Uses `window.LP_BASE_URL` when set; otherwise falls back to
 * production community URL. Evaluated per call so late-injected config still applies.
 */
export function getApiBase(): string {
  if (typeof window === "undefined") return "";
  const raw = window.LP_BASE_URL;
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  return (trimmed || FALLBACK_API_BASE).replace(/\/$/, "");
}

async function readErrorBody(res: Response): Promise<string> {
  try {
    const text = await res.text();
    return text ? ` - ${text.slice(0, 200)}` : "";
  } catch {
    return "";
  }
}

const DEFAULT_PLAYER_LOGIN_URL = "https://community.gcsewithrosi.co.uk/user/studentcourse";

export function getDefaultPlayerLoginUrl(): string {
  return DEFAULT_PLAYER_LOGIN_URL;
}

/**
 * Player session check. With `token`: one-time link, no cookies. Without: session cookie.
 * Backend returns 200 JSON (authenticated or not).
 */
export async function fetchPlayerAuthCheck(options?: { token?: string }): Promise<PlayerAuthCheckResponse> {
  const token = options?.token?.trim();
  const url = token
    ? `${getApiBase()}/api/player/auth-check?token=${encodeURIComponent(token)}`
    : `${getApiBase()}/api/player/auth-check`;
  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: token ? "omit" : "include",
  });
  // Backend must return 200 JSON — a 302 to the HTML login page breaks fetch + JSON (browser follows redirect).
  if (res.redirected) {
    throw new Error(
      `Auth check was redirected to ${res.url} — the API must return 200 JSON from /api/player/auth-check, not a 302 to a login page. Exclude this route from the "force login" filter in CodeIgniter.`
    );
  }
  const text = await res.text();
  let parsed = parseJsonFlexible(text);
  let raw: unknown = parsed.ok ? parsed.value : null;
  if (!parsed.ok) {
    raw = tryRecoverJsonFromHtml(text);
  }
  if (raw === null || typeof raw !== "object") {
    throw new Error(`Auth check returned invalid JSON (${res.status})`);
  }
  let data = raw as PlayerAuthCheckResponse;
  if (!data.authenticated && !data.login_url?.trim()) {
    data = { ...data, login_url: DEFAULT_PLAYER_LOGIN_URL };
  }
  return data;
}

/** GET course with sections and lessons (for course outline page) */
export async function fetchCourse(courseId: number): Promise<CourseResponse> {
  const res = await fetch(`${getApiBase()}/api/courses/${courseId}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "omit",
  });
  if (!res.ok) {
    const body = await readErrorBody(res);
    throw new Error(`Course ${courseId} request failed (${res.status} ${res.statusText})${body}`);
  }
  return res.json();
}

/** GET single lesson (for player; lesson_content = interactive_slides JSON) */
export async function fetchLesson(courseId: number, lessonId: number): Promise<LessonResponse> {
  const res = await fetch(`${getApiBase()}/api/courses/${courseId}/lessons/${lessonId}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "omit",
  });
  if (!res.ok) {
    const body = await readErrorBody(res);
    throw new Error(`Lesson ${lessonId} request failed (${res.status} ${res.statusText})${body}`);
  }
  return res.json();
}

export interface TtsRequestOptions {
  voice?: "fable" | "nova" | "shimmer" | "alloy" | "echo" | "onyx";
  accent?: string;
  style?: string;
  audience?: string;
  year_group?: string;
  age_range?: string;
  instructions?: string;
}

type TtsPayload = {
  text: string;
  voice: string;
  accent: string;
  style: string;
  audience?: string;
  year_group?: string;
  age_range?: string;
  instructions?: string;
};

async function parseTtsJson(res: Response): Promise<{ success?: boolean; audio?: string; error?: string }> {
  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    const text = await res.text();
    throw new Error(
      `TTS returned non-JSON (${res.status} ${res.statusText}): ${text.slice(0, 120)}`
    );
  }
  return res.json();
}

async function requestTtsJson(payload: TtsPayload): Promise<string> {
  const res = await fetch(`${getApiBase()}/user/studentcourse/tts`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  const data = await parseTtsJson(res);
  if (!res.ok || !data.success || !data.audio) {
    throw new Error(data.error || `TTS failed (${res.status} ${res.statusText})`);
  }
  return data.audio;
}

async function requestTtsForm(payload: TtsPayload): Promise<string> {
  const form = new URLSearchParams();
  form.set("text", payload.text);
  form.set("voice", payload.voice);
  form.set("accent", payload.accent);
  form.set("style", payload.style);
  if (payload.audience) form.set("audience", payload.audience);
  if (payload.year_group) form.set("year_group", payload.year_group);
  if (payload.age_range) form.set("age_range", payload.age_range);
  if (payload.instructions) form.set("instructions", payload.instructions);

  const res = await fetch(`${getApiBase()}/user/studentcourse/tts`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
      Accept: "application/json",
    },
    credentials: "include",
    body: form.toString(),
  });
  const data = await parseTtsJson(res);
  if (!res.ok || !data.success || !data.audio) {
    throw new Error(data.error || `TTS failed (${res.status} ${res.statusText})`);
  }
  return data.audio;
}

export async function tts(text: string, options: TtsRequestOptions = {}): Promise<string> {
  const voice = options.voice ?? "fable";
  const payload: TtsPayload = {
    text,
    voice,
    accent: options.accent ?? "british",
    style: options.style ?? "tutor",
    audience: options.audience,
    year_group: options.year_group,
    age_range: options.age_range,
    instructions:
      options.instructions ??
      "Use a natural, warm British English accent. Speak clearly like a friendly UK tutor for GCSE and EAL learners.",
  };
  try {
    return await requestTtsJson(payload);
  } catch (firstError) {
    try {
      return await requestTtsForm(payload);
    } catch {
      throw firstError;
    }
  }
}

/** Auth-free speech-to-text — multipart `audio` only. Client scores the transcript. */
export async function whisperTranscribe(audioBlob: Blob): Promise<{
  success: boolean;
  transcript: string;
  error?: string;
}> {
  const form = new FormData();
  form.append("audio", audioBlob);

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), WHISPER_TIMEOUT_MS);

  try {
    const res = await fetch(`${getApiBase()}/user/studentcourse/whisper_transcribe`, {
      method: "POST",
      body: form,
      credentials: "include",
      signal: controller.signal,
    });

    const text = await res.text();
    let data: Record<string, unknown> = {};
    try {
      data = JSON.parse(text) as Record<string, unknown>;
    } catch {
      return {
        success: false,
        transcript: "",
        error: "Invalid response from speech service",
      };
    }

    const transcript = String(data.transcript ?? "").trim();
    const errMsg = data.error != null ? String(data.error) : undefined;
    const apiSuccess = Boolean(data.success) && res.ok;

    if (!res.ok) {
      return {
        success: false,
        transcript: "",
        error: errMsg || `Request failed (${res.status})`,
      };
    }

    return {
      success: apiSuccess,
      transcript,
      error: apiSuccess ? undefined : errMsg,
    };
  } catch (e) {
    if (e instanceof Error && e.name === "AbortError") {
      return {
        success: false,
        transcript: "",
        error: "Speech recognition timed out — try again.",
      };
    }
    return {
      success: false,
      transcript: "",
      error: e instanceof Error ? e.message : "Network error",
    };
  } finally {
    clearTimeout(timer);
  }
}

export function lessonPhotoUrl(query: string): string {
  const q = encodeURIComponent(query);
  return `${getApiBase()}/user/studentcourse/lesson_photo?q=${q}`;
}

export async function markAsComplete(lessonId: number, courseId: number): Promise<void> {
  await fetch(`${getApiBase()}/user/studentcourse/markascomplete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ lesson_id: lessonId, course_id: courseId }),
    credentials: "include",
  });
}

/** Save slide position for resume analytics (requires logged-in student session). */
export async function saveLessonProgress(
  courseId: number,
  lessonId: number,
  body: { current_slide_index: number; total_slides: number }
): Promise<void> {
  if (!courseId || !lessonId) return;
  void fetch(`${getApiBase()}/user/studentcourse/lesson_progress`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "include",
    body: JSON.stringify({
      course_id: courseId,
      lesson_id: lessonId,
      current_slide_index: body.current_slide_index,
      total_slides: body.total_slides,
    }),
  }).catch(() => {
    /* not logged in or network — UX continues */
  });
}

/** Some backends wrap JSON as `{ data: T }`. */
function unwrapJsonPayload<T>(raw: unknown): T {
  if (!raw || typeof raw !== "object") return {} as T;
  const o = raw as Record<string, unknown>;
  if ("data" in o && o.data != null && typeof o.data === "object" && !Array.isArray(o.data)) {
    return o.data as T;
  }
  return o as T;
}

/**
 * Balanced `{ ... }` from `start` (must point at `{`) with string-aware brace matching.
 */
function extractBalancedObjectAt(text: string, start: number): string | null {
  if (text[start] !== "{") return null;
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < text.length; i++) {
    const c = text[i];
    if (escape) {
      escape = false;
      continue;
    }
    if (c === "\\" && inString) {
      escape = true;
      continue;
    }
    if (c === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;
    if (c === "{") depth++;
    else if (c === "}") {
      depth--;
      if (depth === 0) return text.slice(start, i + 1);
    }
  }
  return null;
}

/**
 * Try every `{` in the body — HTML/CSS often has a `{` before the real API JSON
 * (e.g. `<style>`, inline styles). First `{` is not always the JSON object.
 */
function parseJsonFlexible(text: string): { ok: true; value: unknown } | { ok: false } {
  const trimmed = text.replace(/^\uFEFF/, "").trim();
  if (!trimmed) return { ok: false };
  try {
    return { ok: true, value: JSON.parse(trimmed) };
  } catch {
    /* fall through */
  }
  for (let i = 0; i < trimmed.length; i++) {
    if (trimmed[i] !== "{") continue;
    const chunk = extractBalancedObjectAt(trimmed, i);
    if (!chunk || chunk.length < 2) continue;
    try {
      return { ok: true, value: JSON.parse(chunk) };
    } catch {
      continue;
    }
  }
  /* Last resort: first `{` to last `}` (legacy servers) */
  const start = trimmed.indexOf("{");
  const end = trimmed.lastIndexOf("}");
  if (start >= 0 && end > start) {
    try {
      return { ok: true, value: JSON.parse(trimmed.slice(start, end + 1)) };
    } catch {
      /* fall through */
    }
  }
  return { ok: false };
}

/**
 * Some servers wrap JSON in an HTML shell (error page with `<pre>{...}</pre>` or a JSON script tag).
 * Runs only in the browser where `DOMParser` exists.
 */
function tryRecoverJsonFromHtml(text: string): unknown | null {
  if (typeof DOMParser === "undefined" || !text.includes("<")) return null;
  try {
    const doc = new DOMParser().parseFromString(text, "text/html");
    const jsonScripts = doc.querySelectorAll(
      'script[type="application/json"], script[type="application/ld+json"]'
    );
    for (const node of jsonScripts) {
      const raw = node.textContent?.trim();
      if (!raw) continue;
      const p = parseJsonFlexible(raw);
      if (p.ok) return p.value;
    }
    const pre = doc.querySelector("pre");
    if (pre?.textContent) {
      const p = parseJsonFlexible(pre.textContent.trim());
      if (p.ok) return p.value;
    }
    const bodyText = doc.body?.textContent?.trim();
    if (bodyText?.includes("{")) {
      const p = parseJsonFlexible(bodyText);
      if (p.ok) return p.value;
    }
  } catch {
    return null;
  }
  return null;
}

interface ReadJsonResult {
  raw: unknown;
  text: string;
  /** True when body is empty, not JSON, or only garbage (HTML/plain text). */
  jsonParseFailed: boolean;
  /** Human hint when parse failed (HTML error page vs raw text). */
  parseErrorMessage?: string;
}

async function readResponseJson(res: Response): Promise<ReadJsonResult> {
  const text = await res.text();
  if (!text.trim()) {
    return { raw: {}, text, jsonParseFailed: true, parseErrorMessage: "Empty response from server." };
  }
  const parsed = parseJsonFlexible(text);
  if (parsed.ok) {
    return { raw: parsed.value, text, jsonParseFailed: false };
  }
  const recovered = tryRecoverJsonFromHtml(text);
  if (recovered !== null && typeof recovered === "object") {
    return { raw: recovered, text, jsonParseFailed: false };
  }
  const trimmed = text.trimStart();
  const looksHtml = trimmed.startsWith("<") || trimmed.startsWith("<!");
  const msg = looksHtml
    ? "We couldn’t load that result. Refresh the page, or sign in on the course site and try again."
    : text.slice(0, 400);
  return { raw: {}, text, jsonParseFailed: true, parseErrorMessage: msg };
}

/** PHP / CodeIgniter / Laravel often use `error`, `message`, or nested `errors`. */
function pickApiMessage(...objs: unknown[]): string | undefined {
  for (const obj of objs) {
    if (!obj || typeof obj !== "object") continue;
    const o = obj as Record<string, unknown>;
    for (const key of ["message", "error", "msg", "detail", "description"]) {
      const v = o[key];
      if (typeof v === "string" && v.trim()) return v.trim();
    }
    const errs = o.errors;
    if (Array.isArray(errs) && errs.length) {
      const e0 = errs[0];
      if (typeof e0 === "string") return e0;
      if (e0 && typeof e0 === "object" && typeof (e0 as { message?: string }).message === "string") {
        return (e0 as { message: string }).message;
      }
    }
    if (errs && typeof errs === "object" && !Array.isArray(errs)) {
      const vals = Object.values(errs as Record<string, unknown>);
      const first = vals[0];
      if (typeof first === "string") return first;
    }
  }
  return undefined;
}

/** Backend sometimes returns a useless generic string — give a clearer hint. */
function humanizeQuizSubmitMessage(msg: string | undefined, status: number): string {
  const t = (msg ?? "").trim().toLowerCase();
  if (t === "unknown error" || t === "unknown" || t === "error" || !t) {
    return `Could not save your quiz (HTTP ${status}). Sign in on the course site, then try again.`;
  }
  return msg!.trim();
}

/** Normalize API or legacy string[] options to `{ id, text }[]` for the player UI. */
function normalizeExitQuizQuestions(raw: ExitQuizApiQuestion[]): ExitQuizApiQuestion[] {
  return raw.map((q) => {
    const anyQ = q as ExitQuizApiQuestion & { prompt?: string };
    const id =
      typeof q.id === "number"
        ? q.id
        : typeof q.id === "string"
          ? Number(q.id) || 0
          : Number(q.id) || 0;
    // Handle `question_text` (new API) and `prompt` (legacy) as fallbacks for `question`
    const questionText = (
      anyQ.question ||
      anyQ.question_text ||
      anyQ.prompt ||
      ""
    ).trim();
    return {
      ...q,
      id,
      question: questionText,
      options: (q.options as unknown as (ExitQuizApiOption | string)[]).map((opt, idx) => {
        if (typeof opt === "string") return { id: idx + 1, text: opt };
        if (opt && typeof opt === "object" && "text" in opt) {
          const oid = typeof opt.id === "number" ? opt.id : idx + 1;
          return { id: oid, text: String(opt.text) };
        }
        return { id: idx + 1, text: String(opt) };
      }),
    };
  });
}

/**
 * Exit quiz after lesson (questions only). 404 = no quiz — skip quiz step.
 * Uses cookies when present (same-site / logged-in); safe for public GET too.
 */
export async function fetchLessonExitQuiz(
  courseId: number,
  lessonId: number
): Promise<ExitQuizApiResponse | null> {
  if (!courseId || !lessonId) return null;
  try {
    const res = await fetch(`${getApiBase()}/api/courses/${courseId}/lessons/${lessonId}/exit-quiz`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "omit",
    });
    if (res.status === 404) return null;
    if (!res.ok) return null;
    const text = await res.text();
    let value: unknown = null;
    const parsed = parseJsonFlexible(text);
    if (parsed.ok) {
      value = parsed.value;
    } else {
      value = tryRecoverJsonFromHtml(text);
    }
    if (value === null || typeof value !== "object") return null;
    const data = unwrapJsonPayload<ExitQuizApiResponse>(value);
    if (!data?.questions?.length) return null;
    const quizId =
      typeof data.quiz_id === "number"
        ? data.quiz_id
        : typeof data.quiz_id === "string"
          ? Number(data.quiz_id)
          : undefined;
    return {
      ...data,
      quiz_id: Number.isFinite(quizId as number) ? (quizId as number) : data.quiz_id,
      questions: normalizeExitQuizQuestions(data.questions),
    };
  } catch {
    return null;
  }
}

/**
 * GET /api/courses/:cId/lessons/:lId/attachments
 * Returns downloadable resources attached to this lesson.
 * No auth required (publicly accessible to enrolled students per backend design).
 * Fails silently — an empty array means no attachments, not an error.
 */
export async function fetchLessonAttachments(
  courseId: number,
  lessonId: number
): Promise<import("@/types/api").LessonAttachment[]> {
  if (!courseId || !lessonId) return [];
  try {
    const res = await fetch(
      `${getApiBase()}/api/courses/${courseId}/lessons/${lessonId}/attachments`,
      { method: "GET", headers: { Accept: "application/json" }, credentials: "omit" }
    );
    if (!res.ok) return [];
    const text = await res.text();
    const parsed = parseJsonFlexible(text);
    const value = parsed.ok ? parsed.value : tryRecoverJsonFromHtml(text);
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

/** Submit exit-quiz answers; server scores and stores attempt. */
export async function submitLessonExitQuiz(
  courseId: number,
  lessonId: number,
  payload: {
    quiz_id?: number;
    /** Array of { question_id, option_id } — option_id is the server-assigned option ID, not the index. */
    answers: { question_id: number; option_id: number }[];
  }
): Promise<ExitQuizSubmitResult> {
  const requestBody: Record<string, unknown> = {
    lesson_id: lessonId,
    course_id: courseId,
    answers: payload.answers,
  };
  if (payload.quiz_id != null && Number.isFinite(Number(payload.quiz_id))) {
    requestBody.quiz_id = Number(payload.quiz_id);
  }
  const res = await fetch(`${getApiBase()}/api/player/submit-quiz`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "include",
    body: JSON.stringify(requestBody),
  });
  const { raw, jsonParseFailed, parseErrorMessage } = await readResponseJson(res);

  if (jsonParseFailed) {
    const hint =
      parseErrorMessage ||
      "Could not read the server response. Try again, or sign in on the course site and retry.";
    if (!res.ok) {
      const needsLogin = res.status === 401 || res.status === 403;
      return {
        success: false,
        message: humanizeQuizSubmitMessage(
          needsLogin ? "Log in to save your score" : hint,
          res.status
        ),
        requiresLogin: needsLogin,
      };
    }
    /* HTTP 200 but still unreadable — show a short learner-facing message only */
    return {
      success: false,
      message: humanizeQuizSubmitMessage(hint, res.status),
    };
  }

  const rawBody = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const body = unwrapJsonPayload<ExitQuizSubmitResult>(raw) as ExitQuizSubmitResult;
  const inner = body as unknown as Record<string, unknown>;

  const combinedMsg = pickApiMessage(inner, rawBody);

  if (!res.ok) {
    const needsLogin = res.status === 401 || res.status === 403;
    return {
      success: false,
      message: humanizeQuizSubmitMessage(
        needsLogin ? combinedMsg || "Log in to save your score" : combinedMsg || `Submit failed (${res.status})`,
        res.status
      ),
      requiresLogin: needsLogin,
    };
  }

  // Many APIs return HTTP 200 without a `success` field — treat as success unless explicitly false.
  const sFlag = inner.success ?? rawBody.success;
  const explicitFail =
    sFlag === false ||
    sFlag === 0 ||
    String(sFlag).toLowerCase() === "false";

  if (explicitFail) {
    return {
      message: humanizeQuizSubmitMessage(
        combinedMsg ||
          pickApiMessage(inner) ||
          "Quiz could not be saved. Check you are signed in and try again.",
        res.status
      ),
      success: false,
    };
  }

  // Some backends return 200 + { error: "…" } without success:false — treat as failure if no scores.
  const errStr =
    (typeof inner.error === "string" && inner.error.trim()) ||
    (typeof rawBody.error === "string" && rawBody.error.trim()) ||
    "";
  const hasScores =
    (body.results?.length ?? 0) > 0 ||
    typeof body.score === "number" ||
    typeof body.total === "number" ||
    typeof body.percent === "number";
  if (errStr && !hasScores) {
    return {
      success: false,
      message: humanizeQuizSubmitMessage(errStr, res.status),
    };
  }

  const results: ExitQuizSubmitResult["results"] =
    body.results ?? (inner.result as ExitQuizSubmitResult["results"] | undefined);

  const rawCorrect = inner.correct ?? rawBody.correct;
  const correct =
    typeof rawCorrect === "number"
      ? rawCorrect
      : rawCorrect != null && rawCorrect !== ""
        ? Number(rawCorrect)
        : undefined;

  const rawTotal = body.total ?? body.max_score;
  const total =
    typeof rawTotal === "number"
      ? rawTotal
      : rawTotal != null && rawTotal !== ""
        ? Number(rawTotal)
        : undefined;

  const rawScore = body.score;
  const score =
    typeof rawScore === "number"
      ? rawScore
      : rawScore != null && rawScore !== ""
        ? Number(rawScore)
        : undefined;

  const rawPct = body.percent ?? body.score_percent;
  const percent =
    typeof rawPct === "number"
      ? rawPct
      : rawPct != null && rawPct !== ""
        ? Number(rawPct)
        : undefined;

  return {
    ...body,
    success: true,
    score: Number.isFinite(score as number) ? score : undefined,
    total: Number.isFinite(total as number) ? total : undefined,
    correct: Number.isFinite(correct as number) ? correct : undefined,
    percent: Number.isFinite(percent as number) ? percent : undefined,
    results,
  };
}
